// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
import { TrailerFormModal } from '~/components/module/trailer';

// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_TRAILER,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { trailerActions } from '~/thunks/trailer/trailerSlice';
import { IPaginationResponse } from '~/utils/interface/base';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseMoreAction, IHeaderCellType, ISortBy } from '~/utils/interface/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { PermissionString } from '~/utils/interface/permissions';
import { TRAILER_PERMISSIONS } from '~/utils/constants/permissions';
import { IQueryParamTrailer, ITrailer } from '~/utils/interface/trailer';
import { deleteTrailer, getListTrailer } from '~/thunks/trailer/trailerThunk';
// Styles, images, icons
import styles from './trailer.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsTrailer = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onArchive: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<ITrailer>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITrailer) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.name}</Box>
        </Stack>
      ),
    },
    {
      id: 'trailerType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_trailer_type')}</Box>
          <SortComponent name='trailer type' sortType={getSortType('trailer type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITrailer) => <Box className={cx('textValue')}>{record?.trailerType || EMPTY_VALUE}</Box>,
    },
    {
      id: 'rentalStatus',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_rental_status')}</Box>
          <SortComponent name='rental status' sortType={getSortType('rental status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITrailer) => <Box className={cx('textValue')}>{record?.rentalStatus || EMPTY_VALUE}</Box>,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITrailer) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'note',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_note')}</Box>
          <SortComponent name='note' sortType={getSortType('note')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: ITrailer) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.note || EMPTY_VALUE}</Box>
        </Stack>
      ),
    },
    {
      id: 'action',
      label: t('common_text_action', { ns: 'common/base' }),
      minWidth: 100,
      align: 'center',
      render: (record: ITrailer) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onArchive(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Trailer = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateTrailers, canViewTrailers, canEditTrailers, canDeleteTrailers } =
    usePermissions(TRAILER_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/trailer', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isRefreshTrailerList } = useAppSelector((state) => state.trailer);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_TRAILER);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamTrailer>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [trailerList, setTrailerList] = useState<ITrailer[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [idsTrailer, setIdsTrailer] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<ITrailer[]>([]);
  const [idTrailer, setIdTrailer] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isOpenTrailerFormModal, setIsOpenTrailerFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListTrailer(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshTrailerList) return;

    handleGetListTrailer(paramObject);

    dispatch(trailerActions.setRefreshTrailerList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTrailerList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTrailer = (params: IQueryParamTrailer) => {
    setIsLoading(true);

    dispatch(getListTrailer(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setTrailerList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdTrailer(id);
    setIsOpenTrailerFormModal(true);
  };

  const handleArchive = (id: string) => {
    if (!id) return;

    setIdTrailer(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdTrailer(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteTrailer(idTrailer))
      .unwrap()
      .then(() => {
        setIdTrailer(EMPTY_STRING);
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowsSelect = (ids: string[], selectedItems?: ITrailer[]) => {
    setIdsTrailer(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleShowTrailerFormModal = () => {
    setIsOpenTrailerFormModal((prev) => !prev);
    setIdTrailer(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <Box id='trailerPage' className={cx('container')}>
      <BaseToolbar title={t('trailer_title_management')}>
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateTrailers && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowTrailerFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('trailer_text_add_trailer')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewTrailers ? trailerList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsTrailer(t, sortBy, handleSort, handleEdit, handleArchive, canEditTrailers, canDeleteTrailers)}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsTrailer}
          // onSelectChange={handleRowsSelect}
          {...(canEditTrailers ? { onRowClick: handleEdit } : {})}
        />
      </Box>
      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isOpenTrailerFormModal && <TrailerFormModal onClose={handleShowTrailerFormModal} idTrailer={idTrailer} />}
    </Box>
  );
};

export default Trailer;
